import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { mergeMap } from 'rxjs/operators';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BuyerService } from '../../shared/services/buyer/buyer.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { notifyMessage } from '../../shared/constants/notify-message';
import {
  NcBuyer,
  NcBuyerDetails,
} from '../../shared/interfaces/buyer-response.interface';
import { Constants } from '../../shared/constants/buyer-constant';
import { NcSettings } from '../../shared/interfaces/survey.interface';

@Component({
  selector: 'ps-ui-nightcrawler',
  templateUrl: './nightcrawler.component.html',
  styleUrls: ['./nightcrawler.component.css'],
})
export class NightcrawlerComponent implements OnInit, OnDestroy {
  @Input() surveyId!: number;
  @Input() isSurveySyncedOnNC!: boolean;
  @Input() ncSettings: NcSettings | undefined;
  @Input() launchSurveyForm!: FormGroup;
  @Output() isSurveySyncedOnNCChange = new EventEmitter<boolean>();
  @Output() ncSettingsChange = new EventEmitter();
  @Output() generateEntryLink = new EventEmitter();
  @Output() updatePayload = new EventEmitter();
  public form!: FormGroup;
  private _subscriptions: Subscription = new Subscription();
  public ncBuyerConfigs: NcBuyer[] = [];
  public buyerId!: number;
  private _ncToken!: string;
  public selectedConfig!: NcBuyer;
  readonly NC_SECURITY_OPTIONS = Constants.NC_SECURITY_OPTIONS;
  constructor(
    private _fb: FormBuilder,
    private _buyerService: BuyerService,
    private _authService: AuthService,
    private _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this.buyerId = this._authService.user!.cmp;
    this._getBuyerConfigList(this.buyerId);
    this._generateForm();
  }

  private _generateForm() {
    this.form = this._fb.group({
      buyer: this._fb.group({
        id: [''],
        name: [''],
      }),
      security: this._fb.group({
        label: 'off',
        value: 0,
      }),
      buyerName: [''],
      securityKey: [''],
      buyerEntryLink: [''],
    });
  }

  private _getBuyerConfigList(buyerId: number) {
    const configList = this._buyerService
      .getNightcrawlerConfigSettings(buyerId)
      .pipe(
        mergeMap((response) => {
          // fetching token first to get data from Nightcrawler
          this._ncToken = response.data.accessKey;
          return this._buyerService.getNightcrawlerBuyers(this._ncToken);
        })
      )
      .subscribe(
        (data) => {
          this.ncBuyerConfigs = data;
          if (this.ncSettings) {
            // if ncSettings exist then update form with its value
            this._setForm(this.ncSettings);
          }
        },
        (error) => {
          this._toastr.error(notifyMessage.errorMessage.API_ERROR);
        }
      );
    this._subscriptions.add(configList);
  }

  private _setForm(ncSettings: NcSettings) {
    // Set form data
    this.form.patchValue({
      buyer: this.ncBuyerConfigs.find(
        (config) => config.name === ncSettings.buyerName
      ),
      buyerName: ncSettings.buyerName,
      security: this.NC_SECURITY_OPTIONS.find(
        (opt) => opt.value === ncSettings.security?.type || 0
      ),
      securityKey: ncSettings.security?.secretKey,
      buyerEntryLink: ncSettings.buyerEntryLink,
    });
  }

  setBuyerCofig(ncBuyer: NcBuyer) {
    const buyerDetails = this._buyerService
      .getNightcrawlerBuyer(ncBuyer.id, this._ncToken)
      .subscribe(
        (buyer: NcBuyerDetails) => {
          this.form.patchValue({
            buyer: buyer,
            buyerName: buyer.name,
            security: this.NC_SECURITY_OPTIONS.find(
              (opt) => opt.value === buyer.securityType
            ),
            securityKey: buyer.securityKey,
          });
        },
        (error) => {
          this._toastr.error(
            notifyMessage.errorMessage.API_ERROR ||
              notifyMessage.errorMessage.NIGHTCRAWLER.BUYER_INFO_UNAVAILABLE
          );
        }
      );
    this._subscriptions.add(buyerDetails);
  }

  reset() {
    // Reset form and set default value
    this.form.reset({
      security: {
        label: 'off',
        value: 0,
      },
    });
  }

  syncSurveyToNightcrawler() {
    if (this._validate()) {
      const formValue = this.form.value;
      const ncSurveyConfig = this._tranformData();
      const syncConfig = this._buyerService
        .syncSurveyToNightcrawler(ncSurveyConfig)
        .subscribe(
          (liveURL) => {
            this.isSurveySyncedOnNC = true;
            // update ncSettings value in launch survey component
            this.ncSettings = {
              buyerEntryLink: formValue.buyerEntryLink,
              buyerName: formValue.buyerName,
              security: {
                type: Number(formValue.securityType) || 0,
                secretKey:
                  Number(formValue.securityType) !== 0
                    ? formValue.securityKey
                    : null,
              },
            };
            this.ncSettingsChange.emit(this.ncSettings);
            this.isSurveySyncedOnNCChange.emit(true);
            this.generateEntryLink.emit(liveURL);
            // Update live and test URL
            this.launchSurveyForm.patchValue({
              live_url: liveURL,
              test_url: liveURL,
            });
            this.updatePayload.emit();
          },
          (error) => {
            this._toastr.error(
              error.msg ||
                notifyMessage.errorMessage.NIGHTCRAWLER.UNABLE_TO_SYNC
            );
          }
        );
      this._subscriptions.add(syncConfig);
    }
  }

  private _validate() {
    // validate form data
    const formValue = this.form.value;
    const isValidBuyerConfig = [
      () => formValue.buyerEntryLink,
      () => formValue.buyerName,
      () =>
        Number(formValue.security.value) === 0 ||
        (Number(formValue.security.value) > 0 && formValue.securityKey),
    ].every((item) => !!item());
    if (!isValidBuyerConfig) {
      this._toastr.error(
        notifyMessage.errorMessage.NIGHTCRAWLER.MISSING_REDIRECT_SETTINGS
      );
      return false;
    }
    if (!this._isValidURL(formValue.buyerEntryLink)) {
      this._toastr.error(
        notifyMessage.errorMessage.NIGHTCRAWLER.INCORRECT_SURVEY_ENTRY_LINK
      );
      return false;
    }
    if (!this._validateBuyerLink(formValue.buyerEntryLink)) {
      this._toastr.error(
        notifyMessage.errorMessage.NIGHTCRAWLER.INVALID_BUYER_ENTRY_LINK
      );
      return false;
    }
    return true;
  }

  private _tranformData() {
    const formValue = this.form.value;
    return {
      surveyId: this.surveyId,
      ncBuyerConfig: {
        buyerEntryLink: formValue.buyerEntryLink,
        buyerName: formValue.buyerName,
        buyerId: formValue.buyer.id,
        security: {
          type: Number(formValue.securityType) || 0,
          key:
            Number(formValue.securityType) !== 0 ? formValue.securityKey : null,
        },
      },
    };
  }

  private _isValidURL(url: string) {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  }

  private _validateBuyerLink(url: string) {
    const regex = new RegExp(Constants.NC_ENTRY_LINK.REGEX);
    const key = Constants.NC_ENTRY_LINK.KEY;
    if (regex.test(url) && url.includes(key)) {
      return true;
    }
    return false;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
